import { useState, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { SimpleTable } from "@/components/ui/scroll-table";
import { Badge } from "@/components/ui/badge";
import { Can } from "@/components/auth/Can";
import { cn } from "@/lib/utils";
import { useUserTimezone } from "@/hooks/useUserTimezone";
import { shiftRequestApi } from "@/features/shift-request/api";
import {
  REQUEST_STATUS_COLORS,
  REQUEST_TYPE_COLORS,
  requestStatusLabel,
  requestTypeLabel,
  type ShiftRequest,
} from "@/features/shift-request/types";
import { formatShiftTime } from "@/features/shift/types";
import { useShiftTypeLabel } from "@/features/shift/useShiftTypeLabel";
import { formatTime12 } from "../calendar";
import { useMyRequests, type RequestFilter } from "../hooks/useMyRequests";

const FILTER_KEYS: RequestFilter[] = ["all", "pending", "approved", "rejected"];

export function MyRequestsTable() {
  const { t, i18n } = useTranslation('shifts')
  const tz = useUserTimezone();
  const shiftTypeLabel = useShiftTypeLabel();
  const [filter, setFilter] = useState<RequestFilter>("all");
  const { items, total, loading, hasMore, loadMore, refresh } = useMyRequests(filter);

  const cancel = useCallback(async (id: string) => {
    try {
      await shiftRequestApi.cancelRequest(id);
      toast.success(t('myShift.requestsTable.toast.cancelled'));
      refresh();
    } catch (err: any) {
      console.error("[my-shift] cancel request failed", { id, err });
      toast.error(err?.message || t('myShift.requestsTable.toast.failedToCancel'));
    }
  }, [refresh, t]);

  const columns = useMemo(
    () => [
      {
        key: "type",
        header: t('myShift.requestsTable.columns.type'),
        width: "w-24",
        render: (r: ShiftRequest) => {
          const type = r.requestType || r.request_type || "";
          return (
            <Badge
              className={cn(
                REQUEST_TYPE_COLORS[type] || "bg-muted text-foreground",
                "border-0 text-xs font-normal",
              )}
            >
              {requestTypeLabel(type, t) || "—"}
            </Badge>
          );
        },
      },
      {
        key: "shift",
        header: t('myShift.requestsTable.columns.shift'),
        width: "w-44",
        render: (r: ShiftRequest) => {
          const date = r.schedule?.scheduleDate || r.schedule?.schedule_date || "";
          const s = formatShiftTime(r.schedule?.startAt || r.schedule?.start_at || "", tz);
          const e = formatShiftTime(r.schedule?.endAt || r.schedule?.end_at || "", tz);
          const code = r.schedule?.shiftTypeCode || r.schedule?.shift_type_code || "";
          return (
            <div>
              <div className="text-[12.5px] font-medium tabular-nums">{date || "—"}</div>
              {(s || e) && (
                <div className="text-[10.5px] text-muted-foreground tabular-nums">
                  {shiftTypeLabel(code)} {s}–{e}
                </div>
              )}
            </div>
          );
        },
      },
      {
        key: "proposed",
        header: t('myShift.requestsTable.columns.proposed'),
        width: "w-32",
        render: (r: ShiftRequest) =>
          r.payload?.proposed_start_at ? (
            <span className="text-[11.5px] tabular-nums text-amber-700">
              {formatTime12(r.payload.proposed_start_at, tz, i18n.language)}–
              {formatTime12(r.payload.proposed_end_at, tz, i18n.language)}
            </span>
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
      },
      {
        key: "reason",
        header: t('myShift.requestsTable.columns.reason'),
        width: "w-48",
        render: (r: ShiftRequest) => {
          const reviewerNotes = r.reviewerNotes || r.reviewer_notes;
          return (
            <div className="max-w-[12rem]">
              <span className="block truncate text-[12.5px]" title={r.reason}>{r.reason || "—"}</span>
              {r.status === "rejected" && reviewerNotes && (
                <span className="mt-0.5 block truncate text-[11px] text-red-600" title={reviewerNotes}>
                  {t('myShift.requestsTable.rejected', { notes: reviewerNotes })}
                </span>
              )}
            </div>
          );
        },
      },
      {
        key: "status",
        header: t('myShift.requestsTable.columns.status'),
        width: "w-24",
        render: (r: ShiftRequest) => (
          <Badge
            className={cn(
              REQUEST_STATUS_COLORS[r.status] || "bg-muted",
              "border-0 text-xs font-normal",
            )}
          >
            {requestStatusLabel(r.status, t) || t('shiftRequest.reviewDialog.unknown')}
          </Badge>
        ),
      },
      {
        key: "actions",
        header: "",
        width: "w-20",
        render: (r: ShiftRequest) =>
          r.status === "pending" ? (
            <Can perm="pms:schedule_request:cancel_own">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  void cancel(r.id);
                }}
                className="text-[11.5px] font-semibold text-red-600 hover:text-red-700"
              >
                {t('myShift.requestsTable.cancel')}
              </button>
            </Can>
          ) : null,
      },
    ],
    [tz, cancel, t, shiftTypeLabel],
  );

  return (
    <div className="flex h-full flex-col">
      {/* 筛选 pill 栏 */}
      <div className="flex items-center gap-2 border-b border-border px-4 py-2.5">
        {FILTER_KEYS.map((key) => (
          <button
            key={key}
            onClick={() => setFilter(key)}
            className={cn(
              "rounded-full px-3 py-1 text-[11.5px] font-semibold",
              filter === key
                ? "bg-purple-600 text-white"
                : "bg-muted text-muted-foreground",
            )}
          >
            {t(`myShift.requestsTable.filters.${key}`)}
          </button>
        ))}
      </div>

      {/* 表格区域 */}
      <div className="min-h-0 flex-1">
        <SimpleTable
          columns={columns}
          data={items}
          loading={loading}
          selectable={false}
          emptyMessage={t('myShift.requestsTable.noRequests')}
          containerHeight="auto"
          className="h-full"
          hasMore={hasMore}
          onLoadMore={loadMore}
          bottomContent={
            <div className="px-4 py-2 text-xs text-muted-foreground">
              {t('myShift.requestsTable.showing', { count: items.length, total })}
            </div>
          }
        />
      </div>
    </div>
  );
}
